import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { ProductTemplate } from '../types';

export interface NicheRecipeTemplate {
  id: string;
  name: string;
  executionTimeMinutes: number;
  description?: string;
  niche: string;
}

interface DbRecipeTemplate {
  id: string;
  nicho: string;
  nome_receita: string;
  tempo_execucao_minutos: number;
  descricao?: string;
}

interface UseNicheRecipeTemplatesProps {
  userNiche: string;
  existingRecipes: Pick<ProductTemplate, 'name'>[];
  userId: string;
}

function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function getDismissKey(userId: string, niche: string): string {
  return `dismissed_recipe_suggestions_${userId}_${normalizeString(niche)}`;
}

export function useNicheRecipeTemplates({
  userNiche,
  existingRecipes,
  userId,
}: UseNicheRecipeTemplatesProps) {
  const [allTemplates, setAllTemplates] = useState<NicheRecipeTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const key = getDismissKey(userId, userNiche);
      const raw = localStorage.getItem(key);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });

  const dismissSuggestion = useCallback((id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev).add(id);
      try {
        localStorage.setItem(getDismissKey(userId, userNiche), JSON.stringify([...next]));
      } catch { /* localStorage indisponível */ }
      return next;
    });
  }, [userId, userNiche]);

  const fetchTemplates = useCallback(async (isCancelled?: () => boolean) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.from('niche_recipe_templates').select('*');
      if (isCancelled?.()) return;
      if (error) throw error;
      if (data) {
        setAllTemplates((data as DbRecipeTemplate[]).map(item => ({
          id: item.id,
          name: item.nome_receita,
          executionTimeMinutes: item.tempo_execucao_minutos || 60,
          description: item.descricao || undefined,
          niche: item.nicho,
        })));
      }
    } catch (err) {
      console.error('useNicheRecipeTemplates error:', err);
    } finally {
      if (!isCancelled?.()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    fetchTemplates(() => ignore);
    return () => { ignore = true; };
  }, [fetchTemplates]);

  const suggestions = useMemo<NicheRecipeTemplate[]>(() => {
    if (!allTemplates.length) return [];

    const cleanNiche = normalizeString(userNiche);
    const existingNamesSet = new Set(existingRecipes.map(r => normalizeString(r.name)));

    // Filtra por nicho exato + nicho "Geral (Todos os Nichos)" sempre incluso
    return allTemplates.filter(t => {
      const tNiche = normalizeString(t.niche);
      const matchesNiche = tNiche === cleanNiche || tNiche === normalizeString('Geral (Todos os Nichos)');
      return matchesNiche && !existingNamesSet.has(normalizeString(t.name)) && !dismissedIds.has(t.id);
    });
  }, [allTemplates, userNiche, existingRecipes, dismissedIds]);

  return { suggestions, isLoading, dismissSuggestion };
}
